import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {
  EditorState,
  Compartment
} from '@codemirror/state';

import {
  EditorView,
  keymap,
  highlightActiveLine
} from '@codemirror/view';

import { defaultKeymap } from '@codemirror/commands';

import {
  autocompletion,
  CompletionContext,
  startCompletion
} from '@codemirror/autocomplete';

import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';

@Component({
  selector: 'app-code-editor',
  standalone: true,
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss']
})
export class CodeEditorComponent implements AfterViewInit, OnDestroy, OnChanges {

  @ViewChild('editorContainer', { static: true })
  editorContainer!: ElementRef<HTMLDivElement>;

  @Input() value = '';
  @Input() readonly = false;
  @Input() height = '200px';
  @Input() objectsDetails: any[] = [];

  @Output() valueChange = new EventEmitter<string>();
  @Output() editorBlur = new EventEmitter<void>();

  private editorView?: EditorView;
  private sqlTables: Record<string, string[]> = {};

  private editableCompartment = new Compartment();
  private readOnlyCompartment = new Compartment();

  ngAfterViewInit(): void {
    this.buildSqlTableHints();

    const state = EditorState.create({
      doc: this.value || '',
      extensions: [
        keymap.of([
          {
            key: 'Ctrl-Space',
            run: startCompletion
          },
          ...defaultKeymap
        ]),

        sql(),
        oneDark,
        highlightActiveLine(),

        autocompletion({
          activateOnTyping: true,
          override: [this.customCompletion]
        }),

        this.editableCompartment.of(
          EditorView.editable.of(!this.readonly)
        ),

        this.readOnlyCompartment.of(
          EditorState.readOnly.of(this.readonly)
        ),

        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.valueChange.emit(update.state.doc.toString());
          }

          if (update.focusChanged && !update.view.hasFocus) {
            this.editorBlur.emit();
          }
        })
      ]
    });

    this.editorView = new EditorView({
      state,
      parent: this.editorContainer.nativeElement
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['objectsDetails']) {
      this.buildSqlTableHints();
    }

    if (changes['value'] && this.editorView) {
      const currentValue = this.editorView.state.doc.toString();

      if ((this.value || '') !== currentValue) {
        this.setValue(this.value || '');
      }
    }

    if (changes['readonly'] && this.editorView) {
      this.editorView.dispatch({
        effects: [
          this.editableCompartment.reconfigure(
            EditorView.editable.of(!this.readonly)
          ),
          this.readOnlyCompartment.reconfigure(
            EditorState.readOnly.of(this.readonly)
          )
        ]
      });
    }
  }

  ngOnDestroy(): void {
    this.editorView?.destroy();
  }

  private customCompletion = (context: CompletionContext) => {
    this.buildSqlTableHints();

    const cursor = context.pos;
    const fullSql = context.state.doc.toString();

    const line = context.state.doc.lineAt(cursor);
    const lineBeforeCursor = line.text.slice(0, cursor - line.from);

    const aliasMap = this.extractAliasMap(fullSql);

    const aliasMatch = lineBeforeCursor.match(/(\b\w+)\.$/);
    const alias = aliasMatch ? aliasMatch[1] : null;

    if (alias && aliasMap[alias]) {
      const actualTable = aliasMap[alias];
      const columns = this.sqlTables[actualTable] || [];

      return {
        from: cursor,
        options: columns.map(col => ({
          label: col,
          type: 'property',
          apply: col,
          detail: actualTable
        }))
      };
    }

    const word = context.matchBefore(/[\w.]*$/);

    if (!word && !context.explicit) {
      return null;
    }

    const sqlKeywords = [
      'SELECT',
      'FROM',
      'WHERE',
      'JOIN',
      'LEFT JOIN',
      'RIGHT JOIN',
      'INNER JOIN',
      'GROUP BY',
      'ORDER BY',
      'HAVING',
      'LIMIT',
      'INSERT',
      'UPDATE',
      'DELETE',
      'VALUES',
      'SET',
      'AND',
      'OR',
      'AS',
      'ON',
      'IN',
      'LIKE',
      'BETWEEN',
      'IS NULL',
      'IS NOT NULL'
    ];

    const options: any[] = sqlKeywords.map(keyword => ({
      label: keyword,
      type: 'keyword',
      apply: keyword
    }));

    Object.entries(this.sqlTables).forEach(([table, columns]) => {
      options.push({
        label: table,
        type: 'class',
        apply: table
      });

      columns.forEach(column => {
        const finalText =
          table === '_standalone'
            ? column
            : `${table}.${column}`;

        options.push({
          label: finalText,
          type: 'property',
          apply: finalText,
          detail: table === '_standalone' ? '' : table
        });
      });
    });

    return {
      from: word ? word.from : cursor,
      options
    };
  };

  private buildSqlTableHints(): void {
    const details = Array.isArray(this.objectsDetails)
      ? this.objectsDetails
      : [];

    this.sqlTables = details.reduce((acc, item) => {
      const objectName = item.object || item.table || '';
      const attributeName = item.name || '';

      if (!attributeName) {
        return acc;
      }

      if (objectName) {
        if (!acc[objectName]) {
          acc[objectName] = [];
        }

        if (!acc[objectName].includes(attributeName)) {
          acc[objectName].push(attributeName);
        }
      } else {
        if (!acc['_standalone']) {
          acc['_standalone'] = [];
        }

        if (!acc['_standalone'].includes(attributeName)) {
          acc['_standalone'].push(attributeName);
        }
      }

      return acc;
    }, {} as Record<string, string[]>);
  }

  private extractAliasMap(sqlText: string): Record<string, string> {
    const aliasMap: Record<string, string> = {};

    const regex = /\b(?:FROM|JOIN)\s+(`?[\w\d_]+`?)\s+(?:AS\s+)?(\w+)/gi;
    let match;

    while ((match = regex.exec(sqlText)) !== null) {
      const table = match[1].replace(/`/g, '').trim();
      const alias = match[2];

      aliasMap[alias] = table;
    }

    return aliasMap;
  }

  setValue(value: string): void {
    if (!this.editorView) {
      return;
    }

    this.editorView.dispatch({
      changes: {
        from: 0,
        to: this.editorView.state.doc.length,
        insert: value
      }
    });
  }

  getValue(): string {
    return this.editorView?.state.doc.toString() || '';
  }
}